import '../portal/portalTheme.css';
import React from 'react';
import { ArrowRight, ArrowDown, Check } from 'lucide-react';
import SEO from '../SEO';
import LandingFooter from '../LandingFooter';
import LandingNavigation from './LandingNavigation';
import LandingWorkflowPreview from './LandingWorkflowPreview';
import { LandingProductTour, LandingCapabilities } from './LandingProductTour';
import LandingSignupCTA from './LandingSignupCTA';

// Portal-only presentation. Authentication and public routes keep their existing owners.
export default function LandingPageV2({ onLogin, onRegister }) {
  const navigateTo = (path) => {
    window.history.pushState(null, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };
  return (
    <div className="lexa-portal min-h-screen bg-[#faf9f6] text-slate-950">
      <SEO title="AI-Powered Test Automation | Lexa.run" description="Create API and browser tests, organize your team's testing, and understand the results. Discover AI-assisted test automation with Lexa.run." />
      <LandingNavigation onLogin={onLogin} onRegister={onRegister} navigateTo={navigateTo} />
      <main>
        <section className="relative overflow-hidden py-16 sm:py-24 lg:py-28">
          <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
            <div>
              <p className="mb-7 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-violet-700">
                <span aria-hidden="true" className="h-1.5 w-1.5 bg-violet-600" /> Test automation. More human.</p>
              <h1 className="max-w-2xl text-5xl font-semibold leading-[1.06] tracking-[-0.045em] sm:text-6xl lg:text-[76px]">Less manual<br className="hidden sm:block" /> testing.<br />
                <span className="text-violet-700">More forward<br className="hidden sm:block" /> motion.</span>
              </h1>
              <p className="mt-7 max-w-lg text-lg leading-8 text-slate-500">Turn ideas into API and browser tests with AI assistance. Run them, understand the results, and keep your team moving.</p>
              <div className="mt-9 flex flex-wrap items-center gap-5">
                <button onClick={onRegister} className="inline-flex items-center gap-4 rounded-full bg-violet-700 px-7 py-4 text-sm font-semibold text-white transition-colors hover:bg-violet-800">Start testing free <ArrowRight size={17} />
                </button>
                <a href="#product" className="inline-flex items-center gap-2 py-3 text-sm font-semibold text-slate-700 hover:text-violet-700">See how it works <ArrowDown size={15} />
                </a>
              </div>
              <div className="mt-7 flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-500">
                <span className="flex items-center gap-1.5">
                  <Check size={13} className="text-violet-600" /> API & browser testing</span>
                <span className="flex items-center gap-1.5">
                  <Check size={13} className="text-violet-600" /> Built for your whole team</span>
              </div>
            </div>
            <LandingWorkflowPreview />
          </div>
          <div className="mx-auto mt-16 flex max-w-7xl flex-wrap items-center justify-between gap-4 border-t border-slate-200 px-5 pt-6 sm:px-8 lg:mt-20">
            <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">One connected testing workspace</span>
            <p className="text-xs text-slate-500">API Testing <span className="mx-2 text-slate-300">/</span> Flow Testing <span className="mx-2 text-slate-300">/</span> Test Suites <span className="mx-2 text-slate-300">/</span> Projects</p>
          </div>
        </section>
        <LandingProductTour />
        <LandingCapabilities navigateTo={navigateTo} />
        <section className="bg-[#171525] py-20 text-white sm:py-24">
          <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:gap-24">
            <div>
              <p className="mb-5 text-xs font-semibold uppercase tracking-[0.18em] text-violet-300">A shared view of quality</p>
              <h2 className="text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">See the signal.<br />
                <span className="text-violet-300">Decide what’s next.</span>
              </h2>
              <p className="mt-6 max-w-md text-base leading-8 text-slate-400">Bring the conversation back to real results. Your dashboard connects test activity with the projects and failures that deserve a closer look.</p>
            </div>
            <div className="divide-y divide-white/10">
              {[
                ['01', 'Know what needs attention', 'Find failing, stale and untested work without opening every test.'],
                ['02', 'Understand where failures happen', 'Break down results by project and test type, then open the execution history.'],
                ['03', 'Keep the whole team aligned', 'Review project health and recent activity from a shared workspace.'],
              ].map(([number, title, detail]) => <div key={number} className="flex gap-5 py-6 first:pt-0 last:pb-0">
                <span className="pt-1 font-mono text-xs text-violet-400">{number}</span>
                <div>
                  <h3 className="mb-2 text-lg font-medium">{title}</h3>
                  <p className="max-w-sm text-sm leading-7 text-slate-400">{detail}</p>
                </div>
              </div>)}
            </div>
          </div>
        </section>
        <LandingSignupCTA onRegister={onRegister} />
      </main>
      <LandingFooter navigateTo={navigateTo} />
    </div>
  );
}
